import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";

export default function CappedReplyModal({ open, onClose, presence }) {
    const nav = useNavigate();
    const { t } = useLocale();
    const [phase, setPhase] = useState("line1");
    const [packages, setPackages] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!open) return;
        setPhase("line1");
        setErr("");
        const t1 = setTimeout(() => setPhase("line2"), 1600);
        const t2 = setTimeout(() => setPhase("offer"), 3200);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [open]);

    useEffect(() => {
        if (!open || packages) return;
        api.get("/billing/packages").then(({ data }) => setPackages(data)).catch(() => {});
    }, [open, packages]);

    if (!open) return null;

    const echoPkg = packages?.echo;
    const formatPrice = (n) =>
        n != null ? `€${Number(n).toFixed(2).replace(".", ",")}` : "";

    const continueConnection = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan: "echo",
                origin: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setErr(e?.response?.data?.detail || t("capped.cta_err"));
            setBusy(false);
        }
    };

    const seeAllOptions = () => {
        const reason = presence?.name
            ? t("capped.reason_named", { name: presence.name })
            : t("capped.reason_generic");
        nav("/upgrade", { state: { reason } });
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="capped-reply-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
        >
            {/* Backdrop — same dark/gold world, not a popup */}
            <button
                onClick={onClose}
                aria-label="chiudi"
                data-testid="capped-reply-backdrop"
                className="absolute inset-0 bg-ink-deep/94 backdrop-blur-md animate-fade-in-slow"
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(600px 400px at 50% 35%, rgba(200, 161, 101, 0.14), transparent 70%)",
                }}
            />

            {/* Content */}
            <div className="relative z-10 max-w-lg w-full text-center animate-fade-in">
                {presence?.photo_base64 && (
                    <div className="mx-auto mb-8 w-20 h-20 photo-frame rounded-full overflow-hidden opacity-80">
                        <img
                            src={`data:${presence.photo_mime};base64,${presence.photo_base64}`}
                            alt=""
                            className="w-full h-full object-cover grayscale"
                        />
                    </div>
                )}
                <div className="mb-8 flex justify-center">
                    <BrandMark size={44} ceremonial />
                </div>

                <div className="min-h-[8rem]" aria-live="polite">
                    <p
                        data-testid="capped-line-1"
                        className={`font-serif text-3xl md:text-[2.15rem] leading-tight text-ink-textHigh transition-opacity duration-[900ms] ${
                            phase === "line1" ? "opacity-100" : "opacity-50"
                        }`}
                    >
                        {t("capped.line1")}
                    </p>
                    <p
                        data-testid="capped-line-2"
                        className={`mt-5 font-serif italic text-2xl md:text-[1.7rem] leading-tight text-ink-gold transition-opacity duration-[900ms] ${
                            phase === "line1" ? "opacity-0" : "opacity-100"
                        }`}
                    >
                        {t("capped.line2")}
                    </p>
                </div>

                {/* Inline founder offer — appears as the natural continuation */}
                <div
                    data-testid="capped-inline-offer"
                    className={`mt-10 transition-opacity duration-[1100ms] ${
                        phase === "offer" ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                >
                    <div
                        className="mx-auto max-w-sm border border-ink-gold/40 bg-ink-surface/60 backdrop-blur-sm rounded-sm p-6"
                        style={{ boxShadow: "0 0 60px -18px rgba(200,161,101,0.4)" }}
                    >
                        <p className="label-eyebrow text-ink-gold mb-3" data-testid="capped-access-label">
                            {t("capped.founder")}
                        </p>
                        <p className="font-serif text-5xl text-ink-textHigh leading-none" data-testid="capped-price">
                            {echoPkg ? formatPrice(echoPkg.amount) : "€19,99"}
                            <span className="text-ink-textLow text-sm font-sans ml-2 align-middle">{t("capped.per_month")}</span>
                        </p>
                        <p className="mt-3 text-ink-textLow text-xs tracking-[0.12em] uppercase" data-testid="capped-early-access">
                            {t("capped.early")}
                        </p>
                        <p className="mt-5 font-serif italic text-ink-textMid text-base leading-snug">
                            {t("capped.tagline")}
                        </p>

                        <button
                            onClick={continueConnection}
                            disabled={busy}
                            data-testid="capped-cta-stay"
                            className="btn-primary-glow w-full mt-6 text-base py-3.5"
                        >
                            {busy ? t("capped.cta_stay_busy") : t("capped.cta_stay")}
                        </button>

                        {err && (
                            <p className="mt-3 text-ink-danger text-sm font-serif italic" data-testid="capped-error">
                                {err}
                            </p>
                        )}
                    </div>

                    <div className="mt-6 flex flex-col items-center gap-1">
                        <button
                            onClick={seeAllOptions}
                            data-testid="capped-cta-see-all"
                            className="btn-ghost text-sm"
                        >
                            {t("capped.cta_all")}
                        </button>
                        <button
                            onClick={onClose}
                            data-testid="capped-cta-dismiss"
                            className="btn-ghost text-xs text-ink-textLow mt-1"
                        >
                            {t("capped.cta_dismiss")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
